import { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row,
} from "reactstrap";
import { FaArrowRight, FaSearch } from "react-icons/fa";
import Image from "next/image";
import Link from "next/link";

//local imports
import classes from "@/styles/partner/technology.module.css";
import technologyPartners from "@/data/technology-partner.json";
import { technology } from "@/utils/content";
import { countries, subTypes } from "@/utils/types";
import { useIsMobile } from "@/hooks/useIsMobile";

export default function TechnologyPartner() {
  const content = technology.en;
  const isMobile = useIsMobile();

  const [tp, setTp] = useState(technologyPartners);
  const [search, setSearch] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);

  //scroll position
  const [scrollPoint, setScrollpoint] = useState(0);

  useEffect(() => {
    window.addEventListener("scroll", () => {
      setScrollpoint(window.scrollY);
    });
  });

  const onCountryChange = (value: string) => {
    const found = selectedCountries.find((c) => c === value);
    !found
      ? setSelectedCountries((p) => [...p, value])
      : setSelectedCountries((p) => [...p.filter((l) => l !== value)]);
  };

  const onTypeChange = (value: string) => {
    const found = selectedTypes.find((t) => t === value);
    !found
      ? setSelectedTypes((p) => [...p, value])
      : setSelectedTypes((p) => [...p.filter((l) => l !== value)]);
  };

  const filterChange = () => {
    const tp1 =
      selectedCountries.length !== 0
        ? technologyPartners.filter((t) =>
            t.countries.some((c) => selectedCountries.includes(c))
          )
        : technologyPartners;
    const tp2 =
      selectedTypes.length !== 0
        ? technologyPartners.filter((t) => selectedTypes.includes(t.type))
        : tp1;
    setTp(tp2);
  };

  const searchInput = (e: any) => {
    const text = e.target.value.split(" ");
    if (e.target.value.length === 0) {
      setSearch("");
      filterChange();
    } else {
      setSearch(e.target.value);
      const newTp = tp.filter((i) => {
        return text.every((el: any) => {
          return i.name.toLowerCase().includes(el);
        });
      });
      setTp(newTp);
    }
  };

  useEffect(() => {
    filterChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountries, selectedTypes]);

  return (
    <div className={classes.container}>
      <div className={classes.hero}>
        <h1 className={classes.heading}>{content.title}</h1>
        <p className={classes.para}>{content.subTitle}</p>
        <InputGroup className={classes.inputGroup}>
          <Input
            className={classes.input}
            type="search"
            placeholder={content.searchInputPlaceholder}
            onChange={searchInput}
            value={search}
          />
          <InputGroupText className={classes.inputGroupText}>
            <FaSearch />
          </InputGroupText>
        </InputGroup>
      </div>
      <div className={classes.main}>
        <Container>
          <Row>
            <Col xs={12} lg={2}>
              <Card
                className={
                  isMobile
                    ? classes.card
                    : scrollPoint >= 295
                    ? classes.cardFixed
                    : classes.card
                }
              >
                <div className={classes.cardBody}>
                  <h5>{content.filterBy}</h5>
                  {subTypes.map((s) => (
                    <FormGroup key={s.type} check>
                      <Input
                        className={classes.checkBox}
                        type="checkbox"
                        defaultChecked={s.defaultValue}
                        onChange={() => onTypeChange(s.type)}
                      />{" "}
                      <Label className={classes.label} check>
                        {s.label}
                      </Label>
                    </FormGroup>
                  ))}
                  <h5>{content.countries}</h5>
                  {countries.map((c) => (
                    <FormGroup key={c.name} check>
                      <Input
                        className={classes.checkBox}
                        type="checkbox"
                        defaultChecked={c.defaultValue}
                        onChange={() => onCountryChange(c.name)}
                      />{" "}
                      <Label className={classes.label} check>
                        {c.label}
                      </Label>
                    </FormGroup>
                  ))}
                </div>
              </Card>
            </Col>
            <Col xs={12} lg={10}>
              <h2 className={classes.heading2}>{content.featured}</h2>
              <Row className="mt-4">
                {tp.map((t) => {
                  if (t.isFeatured) {
                    return (
                      <Col className="my-2" xs={12} lg={3} key={t.name}>
                        <Link
                          className={classes.wrapperLink}
                          href={`/partners${t.url}`}
                        >
                          <Card className={classes.featureCard}>
                            <div
                              className={classes.cardHeader}
                              style={{ background: t.color }}
                            >
                              {t.label}
                            </div>
                            <div className={classes.cardImg}>
                              <Image
                                className={`img-fluid`}
                                src={t.logoPath}
                                alt={t.name}
                                height={200}
                                width={200}
                              />
                            </div>
                            <CardBody className={classes.cardBody}>
                              <h6>{t.name}</h6>
                              <p>{t.desc}</p>
                            </CardBody>
                            <span className={classes.link}>
                              Learn More{" "}
                              <FaArrowRight className={classes.arrow} />
                            </span>
                          </Card>
                        </Link>
                      </Col>
                    );
                  }
                })}
              </Row>
              <h2 className={classes.heading2}>{content.marketplace}</h2>
              <Row className="mt-4">
                {tp
                  .filter((t) => !t.isFeatured && t.type === "marketplace")
                  .map((t) => (
                    <Col xs={12} lg={4} key={t.name}>
                      <Card className={classes.card2}>
                        <div className={classes.cardImg}>
                          <Image
                            className={`img-fluid`}
                            src={t.logoPath}
                            alt={t.name}
                            height={300}
                            width={200}
                          />
                        </div>
                        <CardBody className={classes.cardBody}>
                          <h6>{t.name}</h6>
                          <p>{t.label}</p>
                        </CardBody>
                        <Link
                          className={classes.link}
                          href={`/partners${t.url}`}
                        >
                          Learn More <FaArrowRight className={classes.arrow} />
                        </Link>
                      </Card>
                    </Col>
                  ))}
              </Row>
              <h2 className={classes.heading2}>{content.ecommerce}</h2>
              <Row className="mt-4">
                {tp
                  .filter((t) => !t.isFeatured && t.type === "eCommerce")
                  .map((t) => (
                    <Col xs={12} lg={4} key={t.name}>
                      <Card className={classes.card2}>
                        <div className={classes.cardImg}>
                          <Image
                            className={`img-fluid`}
                            src={t.logoPath}
                            alt={t.name}
                            height={300}
                            width={200}
                          />
                        </div>
                        <CardBody className={classes.cardBody}>
                          <h6>{t.name}</h6>
                          <p>{t.label}</p>
                        </CardBody>
                        <Link
                          className={classes.link}
                          href={`/partners${t.url}`}
                        >
                          Learn More <FaArrowRight className={classes.arrow} />
                        </Link>
                      </Card>
                    </Col>
                  ))}
              </Row>
              <h2 className={classes.heading2}>{content.other}</h2>
              <Row className="mt-4">
                {tp
                  .filter((t) => !t.isFeatured && t.type === "erpAndAccounting")
                  .map((t) => {
                    return (
                      <Col xs={12} lg={4} key={t.name}>
                        <Card className={classes.card2}>
                          <div className={classes.cardImg}>
                            <Image
                              className={`img-fluid`}
                              src={t.logoPath}
                              alt={t.name}
                              height={150}
                              width={250}
                            />
                          </div>
                          <CardBody className={classes.cardBody}>
                            <h6>{t.name}</h6>
                            <p>{t.label}</p>
                          </CardBody>
                          {/* <Link
                            className={classes.link}
                            href={`/partners${t.url}`}
                          >
                            Learn More{" "}
                            <FaArrowRight className={classes.arrow} />
                          </Link> */}
                        </Card>
                      </Col>
                    );
                  })}
              </Row>
            </Col>
          </Row>
        </Container>
        <div className={classes.footer}>
          <Container>
            <p>
              {content.footer1}&nbsp;
              <a href={`mailto:${content.footerEmail}`}>
                {content.footerEmail}
              </a>
            </p>
            <p>
              {content.footer2}&nbsp;
              <Link href={content.footerLink}>{content.here}</Link>
            </p>
          </Container>
        </div>
      </div>
    </div>
  );
}
